import {
  ROW_HEIGHT,
  GANTT_PACK_GAP_PX,
  DOT_COLUMN_PX,
  ERA_LABEL_BAND,
  EVENT_LABEL_MAX_WIDTH,
  TITLE_WRAP_CHARS,
  LANE_TOP_PAD,
  LANE_BOTTOM_PAD,
  LANE_MIN_HEIGHT,
} from "./chronosConstants";
import { formatEventDateLine, formatPeriodRangeLine } from "./chronosItemLabels";

// Used only by deprecated stackEventsInLane
const LEGACY_LABEL_CAP = 240;

/**
 * Event ids that are start/end of any period on this timeline (suppress duplicate row).
 */
export function collectBoundaryEventIds(items) {
  const s = new Set();
  for (const it of items) {
    if (it.type !== "period") continue;
    if (it.startEventId != null && it.startEventId !== "") s.add(String(it.startEventId));
    if (it.endEventId != null && it.endEventId !== "") s.add(String(it.endEventId));
  }
  return s;
}

export function itemKey(it) {
  return `${it.type}:${String(it.id)}`;
}

export function effectiveLaneIdForItem(it, lanesSorted) {
  if (!lanesSorted || lanesSorted.length === 0) return "";
  if (it.laneId != null && it.laneId !== "") return String(it.laneId);
  return String(lanesSorted[0].id);
}

export function itemInLane(it, lane, lanesSorted) {
  return effectiveLaneIdForItem(it, lanesSorted) === String(lane.id);
}

/**
 * Padded [minT,maxT] in ms from **items only** (stable minimap + pan bounds).
 * - Eras excluded (same as default zoom domain).
 * - Open-ended periods: only {@link startDate} counts toward max (no infinite end).
 * Does **not** merge the current viewport — that made the track grow while panning.
 */
export function computeDataExtentMsWithPadding(items, fallbackStart, fallbackEnd) {
  let dataMinT = Infinity;
  let dataMaxT = -Infinity;
  for (const it of items || []) {
    if (it.type === "period" && it.isEra) continue;
    if (!it.startDate) continue;
    const s = it.startDate.getTime();
    const e = it.endDate ? it.endDate.getTime() : s;
    dataMinT = Math.min(dataMinT, s, e);
    dataMaxT = Math.max(dataMaxT, s, e);
  }
  if (!Number.isFinite(dataMinT)) {
    return {
      minT: fallbackStart.getTime(),
      maxT: fallbackEnd.getTime(),
    };
  }
  const pad = Math.max(864e5, (dataMaxT - dataMinT) * 0.06);
  return { minT: dataMinT - pad, maxT: dataMaxT + pad };
}

/**
 * Keep the view window inside [minT, maxT]. If zoom is wider than the extent, snap to full extent.
 */
export function clampViewWindowToExtent(viewStart, viewEnd, minT, maxT) {
  const span = Math.max(1, viewEnd.getTime() - viewStart.getTime());
  const limitSpan = maxT - minT;
  if (limitSpan <= 0) {
    return { start: new Date(minT), end: new Date(maxT) };
  }
  let s = viewStart.getTime();
  let e = viewEnd.getTime();
  if (span >= limitSpan) {
    return { start: new Date(minT), end: new Date(maxT) };
  }
  s = Math.max(minT, Math.min(s, maxT - span));
  e = s + span;
  return { start: new Date(s), end: new Date(e) };
}

/** Truncate for display (max chars, ellipsis). */
export function truncateEventName(name) {
  const t = String(name || "").trim();
  if (t.length <= 120) return t;
  return `${t.slice(0, 120)}…`;
}

/**
 * Words that would cross the {@link TITLE_WRAP_CHARS} limit move entirely to the next line.
 * At most two lines; overflow after line 2 ends with an ellipsis (never mid-word on line 2).
 */
function line1Words(words, maxLen) {
  if (!words.length) return { line: "", rest: [] };
  if (words[0].length > maxLen) return { line: null, rest: words };
  const acc = [words[0]];
  let len = words[0].length;
  for (let i = 1; i < words.length; i += 1) {
    const w = words[i];
    const add = 1 + w.length;
    if (len + add <= maxLen) {
      acc.push(w);
      len += add;
    } else break;
  }
  return { line: acc.join(" "), rest: words.slice(acc.length) };
}

function line2Words(words, maxLen) {
  if (!words.length) return { line: "", ellipsis: false };
  if (words[0].length > maxLen) {
    return { line: words[0], ellipsis: words.length > 1 };
  }
  const acc = [words[0]];
  let len = words[0].length;
  for (let i = 1; i < words.length; i += 1) {
    const w = words[i];
    const add = 1 + w.length;
    if (len + add <= maxLen) {
      acc.push(w);
      len += add;
    } else break;
  }
  return {
    line: acc.join(" "),
    ellipsis: words.length > acc.length,
  };
}

export function splitTitleLines(name) {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return [""];
  const full = words.join(" ");
  if (full.length <= TITLE_WRAP_CHARS) return [full];

  const { line: l1, rest: r1 } = line1Words(words, TITLE_WRAP_CHARS);

  if (l1 === null) {
    const w0 = r1[0];
    const rest = r1.slice(1);
    const { line: l2, ellipsis } = line2Words(rest, TITLE_WRAP_CHARS);
    const tail = l2 + (ellipsis ? "…" : "");
    if (!tail) return [w0];
    return [w0, tail];
  }

  const { line: l2, ellipsis } = line2Words(r1, TITLE_WRAP_CHARS);
  return [l1, l2 + (ellipsis ? "…" : "")];
}

/** Max width of the title block (two lines, monospace-ish estimate). */
export function estimateTitleBlockWidth(name) {
  const lines = splitTitleLines(truncateEventName(name));
  const charW = 7.2;
  let maxW = 0;
  for (const line of lines) {
    maxW = Math.max(maxW, Math.ceil(line.length * charW) + 2 * 8 + 2);
  }
  return Math.min(EVENT_LABEL_MAX_WIDTH, Math.max(48, maxW));
}

/** Width for a single date line (smaller font in UI). */
export function estimateDateLineWidth(dateStr) {
  const s = String(dateStr || "");
  const charW = 6.5;
  const w = Math.ceil(s.length * charW) + 16;
  return Math.min(EVENT_LABEL_MAX_WIDTH, Math.max(72, w));
}

/** Column width to the right of the dot / bar: title (two lines) or date, whichever is wider. */
export function estimateGanttLabelColumnWidth(name, dateLineStr) {
  const a = estimateTitleBlockWidth(name);
  const b = estimateDateLineWidth(dateLineStr);
  return Math.min(EVENT_LABEL_MAX_WIDTH, Math.max(48, a, b));
}

/**
 * @deprecated use {@link estimateTitleBlockWidth} or {@link estimateGanttLabelColumnWidth}
 */
export function estimateLabelWidth(text) {
  return estimateTitleBlockWidth(text);
}

function intervalsConflict(left, right, lo, ro, gapNorm) {
  return !(right + gapNorm <= lo || ro + gapNorm <= left);
}

/**
 * @param {number} left
 * @param {number} right
 * @param {Array<Array<[number, number]>>} rowIntervals
 * @param {number} gapNorm
 * @returns {number} row index
 */
function assignRow(left, right, rowIntervals, gapNorm) {
  let row = 0;
  while (row < 500) {
    if (!rowIntervals[row]) rowIntervals[row] = [];
    const ok = rowIntervals[row].every(([lo, ro]) =>
      !intervalsConflict(left, right, lo, ro, gapNorm)
    );
    if (ok) {
      rowIntervals[row].push([left, right]);
      return row;
    }
    row += 1;
  }
  return row;
}

/**
 * Event occupancy along the time axis (ms). Pan-invariant: does not use viewStart.
 */
function eventMsExtent(it, spanMs, chartW, boundary, dateMode) {
  if (!it.startDate) return null;
  const sid = String(it.id);
  if (boundary.has(sid)) return null;
  const w = Math.max(320, chartW);
  const t = it.startDate.getTime();
  const dateLine = formatEventDateLine(it.startDate, dateMode);
  const colW = estimateGanttLabelColumnWidth(it.name, dateLine);
  const totalPx = DOT_COLUMN_PX + colW;
  const widthMs = (totalPx / w) * spanMs;
  return { left: t, right: t + widthMs };
}

/**
 * Non-era period occupancy along the time axis (ms).
 * Open-ended: must match rendering — the bar spans to the chart’s right edge, i.e. {@link viewEnd},
 * not `t0 + spanMs` (that only equals the visible end when the period starts exactly at {@link viewStart}).
 */
function periodMsExtent(it, spanMs, chartW, dateMode, viewEnd) {
  if (!it.startDate) return null;
  const w = Math.max(320, chartW);
  const t0 = it.startDate.getTime();
  const ve = viewEnd.getTime();
  const t1 = it.endDate ? it.endDate.getTime() : ve;
  const dateStr = formatPeriodRangeLine(it.startDate, it.endDate, dateMode);
  const textW = estimateGanttLabelColumnWidth(it.name, dateStr);
  const barMs = Math.max(1e-9, t1 - t0);
  const barPx = (barMs / spanMs) * w;
  let right;
  if (barPx >= textW + 12) {
    right = t1;
  } else {
    right = Math.max(t1, t0 + (textW / w) * spanMs);
  }
  return { left: t0, right: Math.max(t0 + 1e-9, right) };
}

function isPackable(it, boundary) {
  if (it.type === "event") {
    if (!it.startDate) return false;
    return !boundary.has(String(it.id));
  }
  if (it.type === "period") {
    if (it.isEra) return false;
    return !!it.startDate;
  }
  return false;
}

/**
 * Pack events + non-era periods in one lane into Gantt rows.
 * Uses millisecond extents and gap (pan-invariant at fixed zoom + chart width).
 * @returns {Array<{ it: object, row: number }>}
 */
export function packGanttItemsInLane(
  itemsInLane,
  boundary,
  viewStart,
  viewEnd,
  chartW,
  dateMode
) {
  const spanMs = Math.max(1, viewEnd.getTime() - viewStart.getTime());
  const w = Math.max(320, chartW);
  const gapMs = (GANTT_PACK_GAP_PX / w) * spanMs;

  const entries = [];
  for (const it of itemsInLane) {
    if (!isPackable(it, boundary)) continue;
    let ext;
    if (it.type === "event") {
      ext = eventMsExtent(it, spanMs, w, boundary, dateMode);
    } else {
      ext = periodMsExtent(it, spanMs, w, dateMode, viewEnd);
    }
    if (!ext) continue;
    entries.push({ it, ...ext });
  }

  entries.sort((a, b) => {
    const ta = a.it.startDate ? a.it.startDate.getTime() : 0;
    const tb = b.it.startDate ? b.it.startDate.getTime() : 0;
    if (ta !== tb) return ta - tb;
    return String(a.it.id).localeCompare(String(b.it.id));
  });

  /** @type {Array<Array<[number, number]>>} */
  const rowIntervals = [];
  const out = [];
  for (const e of entries) {
    const row = assignRow(e.left, e.right, rowIntervals, gapMs);
    out.push({ it: e.it, row });
  }
  return out;
}

export function ganttRowCount(packed) {
  let m = 0;
  for (const p of packed) {
    m = Math.max(m, p.row + 1);
  }
  return m;
}

/**
 * Max stacked rows across all lanes for the current view (for default zoom).
 */
export function ganttMaxStackDepth(
  items,
  lanesSorted,
  boundary,
  viewStart,
  viewEnd,
  chartW,
  dateMode
) {
  let globalMax = 0;
  for (const lane of lanesSorted) {
    const laneItems = items.filter((it) => itemInLane(it, lane, lanesSorted));
    const packed = packGanttItemsInLane(
      laneItems,
      boundary,
      viewStart,
      viewEnd,
      chartW,
      dateMode
    );
    globalMax = Math.max(globalMax, ganttRowCount(packed));
  }
  return globalMax;
}

/**
 * Lane content height: optional era title band + Gantt rows.
 * @param {number} rowCount
 * @param {boolean} hasEraInLane
 */
export function laneContentHeight(rowCount, hasEraInLane) {
  const rows = Math.max(1, rowCount);
  const era = hasEraInLane ? ERA_LABEL_BAND : 0;
  const h =
    era + LANE_TOP_PAD + rows * ROW_HEIGHT + LANE_BOTTOM_PAD;
  return Math.max(LANE_MIN_HEIGHT, h);
}

/** @deprecated use packGanttItemsInLane — kept for transitional imports */
export function stackEventsInLane(entries) {
  const visible = entries.filter((e) => e.show);
  const sorted = [...visible].sort((a, b) => {
    const ta = a.it.startDate ? a.it.startDate.getTime() : 0;
    const tb = b.it.startDate ? b.it.startDate.getTime() : 0;
    if (ta !== tb) return ta - tb;
    return String(a.it.id).localeCompare(String(b.it.id));
  });
  const rowIntervals = [];
  const out = [];
  const gapNorm = 0;
  for (const e of sorted) {
    const left = e.n0;
    const right = left + e.labelWNorm;
    const row = assignRow(left, right, rowIntervals, gapNorm);
    out.push({ it: e.it, row, labelW: LEGACY_LABEL_CAP, show: true });
  }
  return out;
}

export function maxStackDepth(stacked) {
  let m = 0;
  for (const s of stacked) {
    m = Math.max(m, s.row + 1);
  }
  return m;
}

export function periodNameStripHeight() {
  return 0;
}

export function eventStackBaseY(periodStripPx) {
  return (periodStripPx || 0) + LANE_TOP_PAD;
}
